import { GeneratorOptions } from './types';

export const DEFAULT_CHARSET = {
  uppercase: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  lowercase: 'abcdefghijklmnopqrstuvwxyz',
  numbers: '0123456789',
  symbols: '!@#%&*_+-=',
} as const;

export const DEFAULT_PASSWORD_LENGTH = 16;
export const MIN_PASSWORD_LENGTH = 1;
export const MAX_PASSWORD_LENGTH = 256;

/**
 * Validates password generation options
 */
const validateOptions = (options: GeneratorOptions): void => {
  const { length, charset, uppercase, lowercase, numbers, symbols } = options;

  if (length !== undefined) {
    const isValidLength =
      Number.isInteger(length) &&
      length >= MIN_PASSWORD_LENGTH &&
      length <= MAX_PASSWORD_LENGTH;

    if (!isValidLength) {
      throw new Error(
        `Password length must be an integer between ${MIN_PASSWORD_LENGTH} and ${MAX_PASSWORD_LENGTH}`,
      );
    }
  }

  if (charset && !charset.length) {
    throw new Error('Custom charset cannot be empty');
  }

  const hasCustomCharset = !!charset;
  const hasAnyCharsetEnabled = uppercase || lowercase || numbers || symbols;

  if (!hasCustomCharset && !hasAnyCharsetEnabled) {
    throw new Error(
      'At least one character set must be enabled or a custom charset must be provided',
    );
  }
};

/**
 * Builds charset string based on options
 */
const buildCharset = (options: GeneratorOptions): string => {
  const {
    charset: customCharset,
    uppercase,
    lowercase,
    numbers,
    symbols,
  } = options;

  if (customCharset) {
    return customCharset;
  }

  const charsetParts: string[] = [];
  if (uppercase) charsetParts.push(DEFAULT_CHARSET.uppercase);
  if (lowercase) charsetParts.push(DEFAULT_CHARSET.lowercase);
  if (numbers) charsetParts.push(DEFAULT_CHARSET.numbers);
  if (symbols) charsetParts.push(DEFAULT_CHARSET.symbols);

  return charsetParts.join('');
};

/**
 * Generates a cryptographically secure random password based on the provided options
 * Uses Web Crypto API for secure random number generation
 *
 * @param options - Password generation options
 * @returns A securely generated password string
 * @throws {Error} If options are invalid or password generation fails
 */
export const generatePassword = (options: GeneratorOptions = {}): string => {
  validateOptions(options);

  const length = options.length ?? DEFAULT_PASSWORD_LENGTH;
  const charset = buildCharset(options);

  if (!charset.length) {
    throw new Error('Charset cannot be empty');
  }

  try {
    const randomValues = new Uint32Array(length);
    crypto.getRandomValues(randomValues);

    const charsetLength = charset.length;
    const passwordChars: string[] = [];

    for (let i = 0; i < length; i++) {
      const randomIndex = randomValues[i] % charsetLength;
      passwordChars.push(charset[randomIndex]);
    }

    return passwordChars.join('');
  } catch (error) {
    const errorMessage =
      error instanceof Error ? error.message : 'Unknown error';
    throw new Error(`Failed to generate password: ${errorMessage}`);
  }
};
